import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import SupportClient, {
    AdminDashboardSummary,
    AdminOrganizationSummary,
    AzureCostDashboard,
    SupportAssignee,
    SupportRequestPriority,
    SupportRequestStatus,
    SupportRequestSummary,
    SupportRequestType,
} from '../apiClients/SupportClient';
import BusySpinner from '../components/BusySpinner';
import { useErrorDisplay } from '../components/ErrorDisplay';
import PageTitle from '../components/PageTitle';
import PanelSegment from '../components/PanelSegment';
import {
    ActivityTimeline,
    PriorityChip,
    RequestTypeIcon,
    StatusChip,
    supportStatusLabel,
} from '../components/support/SupportRequestVisuals';
import useViewportMode from '../hooks/useViewportMode';
import { MobileEntityCard, ResponsiveDialog, StickyDialogActions } from '../components/responsive';
import AzureCostsView from './system-administration/AzureCostsView';

export type SystemAdministrationView = 'overview' | 'requests' | 'organizations' | 'costs';

interface SystemAdministrationPageProps {
    view: SystemAdministrationView;
}

const requestStatuses: SupportRequestStatus[] = ['Submitted', 'WaitingForClient', 'Triaged', 'Planned', 'InProgress', 'Resolved', 'NotPlanned', 'Duplicate', 'Closed'];
const requestPriorities: SupportRequestPriority[] = ['Low', 'Normal', 'High', 'Urgent'];
const allowedTransitions: Record<SupportRequestStatus, SupportRequestStatus[]> = {
    Submitted: ['WaitingForClient', 'Triaged', 'Closed'],
    WaitingForClient: ['Submitted', 'Triaged', 'Closed'],
    Triaged: ['Planned', 'NotPlanned', 'Duplicate', 'Closed'],
    Planned: ['InProgress', 'Closed'],
    InProgress: ['Resolved', 'Closed'],
    Resolved: ['InProgress', 'Closed'],
    NotPlanned: ['Triaged', 'Closed'],
    Duplicate: ['Triaged', 'Closed'],
    Closed: ['Submitted'],
};

const SystemAdministrationPage = ({ view }: SystemAdministrationPageProps) => {
    const { mode: viewportMode, isDesktop } = useViewportMode();
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [dashboard, setDashboard] = useState<AdminDashboardSummary>();
    const [organizations, setOrganizations] = useState<AdminOrganizationSummary[]>([]);
    const [requests, setRequests] = useState<SupportRequestSummary[]>([]);
    const [assignees, setAssignees] = useState<SupportAssignee[]>([]);
    const [azureCosts, setAzureCosts] = useState<AzureCostDashboard>();
    const [costPeriod, setCostPeriod] = useState(() => dayjs().format('YYYY-MM'));
    const [selected, setSelected] = useState<SupportRequestSummary>();
    const [busy, setBusy] = useState(false);
    const [query, setQuery] = useState('');
    const [status, setStatus] = useState<SupportRequestStatus | 'All'>('All');
    const [type, setType] = useState<SupportRequestType | 'All'>('All');

    const loadData = async (signal?: AbortSignal, forceRefresh = false) => {
        try {
            setBusy(true);
            const client = new SupportClient();
            if (view === 'costs') {
                const costs = await client.GetAdminAzureCosts(costPeriod || undefined, forceRefresh, signal);
                setAzureCosts(costs);
                return;
            }
            const [dashboardData, organizationData, requestData, assigneeData] = await Promise.all([
                client.GetAdminDashboard(signal),
                client.GetAdminOrganizations(signal),
                client.GetAdminRequests({}, signal),
                client.GetAssignees(signal),
            ]);
            setDashboard(dashboardData);
            setOrganizations(organizationData);
            setRequests(requestData);
            setAssignees(assigneeData);
            setSelected(current => current ? requestData.find(item => item.Id === current.Id) : undefined);
        } catch (error) {
            if (!signal?.aborted)
                setError(error, 'Unable to load System Administration');
        } finally {
            if (!signal?.aborted)
                setBusy(false);
        }
    };

    useEffect(() => {
        const controller = new AbortController();
        loadData(controller.signal);
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, costPeriod]);

    const titles: Record<SystemAdministrationView, { title: string; subtitle: string }> = {
        overview: { title: 'System Administration', subtitle: 'Platform health, customer success, revenue, and product delivery in one operating view.' },
        requests: { title: 'Work Queue', subtitle: 'Triage and manage customer support requests across the platform.' },
        organizations: { title: 'Organizations', subtitle: 'Manage organizations, subscriptions, and platform access.' },
        costs: { title: 'Azure Costs', subtitle: 'Track actual cloud spend, historical trends, and projected month-end cost.' },
    };

    const filteredRequests = useMemo(() => requests.filter(request => {
        const needle = query.toLowerCase().trim();
        return (status === 'All' || request.Status === status)
            && (type === 'All' || request.Type === type)
            && (!needle || `${request.Number} ${request.Title} ${request.OrganizationName} ${request.ReporterName}`.toLowerCase().includes(needle));
    }), [query, requests, status, type]);

    const filteredOrganizations = useMemo(() => {
        const needle = query.toLowerCase().trim();
        return organizations.filter(organization => !needle || organization.Name.toLowerCase().includes(needle));
    }, [organizations, query]);

    const refreshRequest = async (id: string) => {
        const updated = await new SupportClient().GetAdminRequest(id);
        setSelected(updated);
        setRequests(items => items.map(item => item.Id === updated.Id ? updated : item));
    };

    const openRequest = async (request: SupportRequestSummary) => {
        try {
            setBusy(true);
            setSelected(await new SupportClient().GetAdminRequest(request.Id));
        } catch (error) {
            setError(error, 'Unable to load request details');
        } finally {
            setBusy(false);
        }
    };

    const setSubscriptionNotRequired = async (organization: AdminOrganizationSummary, enabled: boolean) => {
        const action = enabled ? 'grant subscription-free access to' : 'require a subscription for';
        if (!window.confirm(`Are you sure you want to ${action} ${organization.Name}?`))
            return;

        try {
            setBusy(true);
            await new SupportClient().SetSubscriptionNotRequired(organization.Id, enabled);
            setOrganizations(items => items.map(item => item.Id === organization.Id
                ? { ...item, SubscriptionNotRequired: enabled }
                : item));
        } catch (error) {
            setError(error, 'Unable to update subscription access');
        } finally {
            setBusy(false);
        }
    };

    return <>
        <ErrorDisplay />
        <BusySpinner component={Paper} busy={busy}>
            <PanelSegment
                titleContent={<Box>
                    <PageTitle title={titles[view].title} />
                    <Typography color="text.secondary">{titles[view].subtitle}</Typography>
                </Box>}
                toolbar={<Button startIcon={<RefreshRoundedIcon />} onClick={() => loadData(undefined, view === 'costs')} sx={{ minHeight: isDesktop ? undefined : 44 }}>Refresh</Button>}
                fillHeight>
                <Box data-viewport-mode={viewportMode} sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1600, mx: 'auto' }}>
                    {view === 'overview' && <OperationsOverview dashboard={dashboard} organizations={organizations} requests={requests} onOpenRequest={openRequest} />}
                    {view === 'requests' && <RequestQueue requests={filteredRequests} query={query} setQuery={setQuery} status={status} setStatus={setStatus} type={type} setType={setType} onSelect={openRequest} />}
                    {view === 'organizations' && <OrganizationDirectory organizations={filteredOrganizations} query={query} setQuery={setQuery} onSetSubscriptionNotRequired={setSubscriptionNotRequired} />}
                    {view === 'costs' && <AzureCostsView dashboard={azureCosts} period={costPeriod} onPeriodChange={setCostPeriod} />}
                </Box>
            </PanelSegment>
        </BusySpinner>
        <RequestTriageDialog request={selected} assignees={assignees} onClose={() => setSelected(undefined)} onRefresh={refreshRequest} onBusy={setBusy} onError={setError} />
    </>;
};

const money = (cents?: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format((cents || 0) / 100);

const OperationsOverview = ({ dashboard, organizations, requests, onOpenRequest }: {
    dashboard?: AdminDashboardSummary;
    organizations: AdminOrganizationSummary[];
    requests: SupportRequestSummary[];
    onOpenRequest: (request: SupportRequestSummary) => void;
}) => {
    const { isPhone, isTablet, isLandscape } = useViewportMode();
    const attention = requests.filter(item => ['Submitted', 'WaitingForClient'].includes(item.Status)).slice(0, 6);
    const healthiest = [...organizations].sort((a, b) => b.EstimatedMonthlyRevenueCents - a.EstimatedMonthlyRevenueCents).slice(0, 5);
    return <Stack spacing={3}>
        <Grid container spacing={2}>
            <MetricCard label="Monthly recurring revenue" value={money(dashboard?.EstimatedMonthlyRevenueCents)} helper={`${money(dashboard?.EstimatedAnnualRevenueCents)} annualized`} icon={<AccountBalanceRoundedIcon />} color="#0f9d72" />
            <MetricCard label="Active subscriptions" value={dashboard?.ActiveSubscriptions || 0} helper={`${dashboard?.TotalOrganizations || 0} total organizations`} icon={<ApartmentRoundedIcon />} color="#3273dc" />
            <MetricCard label="Active teammates" value={dashboard?.ActiveTeammates || 0} helper={`${dashboard?.ActiveUsers || 0} application users`} icon={<GroupsRoundedIcon />} color="#e18b2d" />
            <MetricCard label="Open requests" value={dashboard?.OpenRequests || 0} helper={`${dashboard?.UrgentRequests || 0} urgent`} icon={<AssignmentLateOutlinedIcon />} color="#d75252" />
        </Grid>
        <Grid container spacing={2}>
            <Grid item xs={12} sm={isTablet && isLandscape ? 7 : 12} lg={7}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                        <Stack direction={isPhone ? 'column' : 'row'} gap={1} justifyContent="space-between" alignItems={isPhone ? 'flex-start' : 'center'} sx={{ mb: 1.5 }}><Box><Typography variant="h5">Needs attention</Typography><Typography variant="body2" color="text.secondary">New intake and requests waiting for follow-up.</Typography></Box><Chip label={`${attention.length} shown`} variant="outlined" /></Stack>
                        {attention.length === 0 && <Alert severity="success">The intake queue is clear.</Alert>}
                        <Stack divider={<Divider flexItem />}>
                            {attention.map(request => <CardActionArea key={request.Id} onClick={() => onOpenRequest(request)} sx={{ borderRadius: 1.5 }}>
                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1.5 }}>
                                    <RequestTypeIcon type={request.Type} small />
                                    <Box sx={{ minWidth: 0, flex: 1 }}><Typography fontWeight={700} noWrap>{request.Title}</Typography><Typography variant="caption" color="text.secondary">{request.OrganizationName} · REQ-{request.Number}</Typography></Box>
                                    <StatusChip status={request.Status} />
                                </Stack>
                            </CardActionArea>)}
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12} sm={isTablet && isLandscape ? 5 : 12} lg={5}>
                <Card variant="outlined" sx={{ height: '100%' }}><CardContent>
                    <Typography variant="h5">Customer portfolio</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Highest estimated monthly recurring revenue.</Typography>
                    <Stack divider={<Divider flexItem />}>{healthiest.map(organization => <Stack key={organization.Id} direction={isPhone ? 'column' : 'row'} gap={0.5} justifyContent="space-between" alignItems={isPhone ? 'flex-start' : 'center'} sx={{ py: 1.25 }}><Box><Typography fontWeight={700}>{organization.Name}</Typography><Typography variant="caption" color="text.secondary">{organization.BilledTeammateCount} billed teammates · {organization.UserCount} users</Typography></Box><Typography fontWeight={800}>{money(organization.EstimatedMonthlyRevenueCents)}</Typography></Stack>)}</Stack>
                </CardContent></Card>
            </Grid>
        </Grid>
    </Stack>;
};

const MetricCard = ({ label, value, helper, icon, color }: { label: string; value: string | number; helper: string; icon: React.ReactNode; color: string }) => {
    const { isTablet, isLandscape, isDesktop } = useViewportMode();
    return <Grid item xs={12} sm={isTablet ? (isLandscape ? 6 : 12) : 6} xl={isDesktop ? 3 : undefined}><Card variant="outlined" sx={{ height: '100%', overflow: 'hidden', position: 'relative' }}><Box sx={{ position: 'absolute', inset: '0 auto 0 0', width: 5, bgcolor: color }} /><CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center' }}><Avatar sx={{ bgcolor: color, width: 48, height: 48 }}>{icon}</Avatar><Box sx={{ minWidth: 0 }}><Typography variant="overline" color="text.secondary">{label}</Typography><Typography variant="h3">{value}</Typography><Typography variant="caption" color="text.secondary">{helper}</Typography></Box></CardContent></Card></Grid>;
};

const RequestQueue = ({ requests, query, setQuery, status, setStatus, type, setType, onSelect }: {
    requests: SupportRequestSummary[];
    query: string;
    setQuery: (value: string) => void;
    status: SupportRequestStatus | 'All';
    setStatus: (value: SupportRequestStatus | 'All') => void;
    type: SupportRequestType | 'All';
    setType: (value: SupportRequestType | 'All') => void;
    onSelect: (request: SupportRequestSummary) => void;
}) => {
    const { mode, isPhone, isTablet, isPortrait, isLandscape, isDesktop } = useViewportMode();
    const stackFilters = isPhone || (isTablet && isPortrait);
    const tabletColumns = isTablet && isLandscape ? 6 : 12;
    return <Stack spacing={2} data-viewport-mode={mode}>
        <Card variant="outlined"><CardContent sx={{ display: 'flex', flexDirection: stackFilters ? 'column' : 'row', gap: 1.5, flexWrap: 'wrap', alignItems: stackFilters ? 'stretch' : 'center' }}>
            <TextField size="small" label="Search queue" value={query} onChange={event => setQuery(event.target.value)} sx={{ minWidth: stackFilters ? 0 : 240, flex: 1, '& .MuiInputBase-root': { minHeight: isDesktop ? undefined : 44 } }} />
            <FormControl size="small" sx={{ minWidth: stackFilters ? 0 : 170, '& .MuiInputBase-root': { minHeight: isDesktop ? undefined : 44 } }}><InputLabel>Status</InputLabel><Select label="Status" value={status} onChange={event => setStatus(event.target.value as any)}><MenuItem value="All">All statuses</MenuItem>{requestStatuses.map(item => <MenuItem key={item} value={item}>{supportStatusLabel(item)}</MenuItem>)}</Select></FormControl>
            <ToggleButtonGroup fullWidth={!isDesktop} size="small" exclusive value={type} onChange={(_, value) => value && setType(value)} sx={{ '& .MuiToggleButton-root': { minHeight: isDesktop ? undefined : 44, flex: isDesktop ? undefined : 1 } }}><ToggleButton value="All">All</ToggleButton><ToggleButton value="Issue">Issues</ToggleButton><ToggleButton value="FeatureRequest">Features</ToggleButton></ToggleButtonGroup>
        </CardContent></Card>
        <Grid container spacing={1.5}>
            {requests.map(request => <Grid item xs={12} sm={isTablet ? tabletColumns : 12} md={isDesktop ? 6 : undefined} xl={isDesktop ? 4 : undefined} key={request.Id}><Card variant="outlined" sx={{ height: '100%' }}><CardActionArea onClick={() => onSelect(request)} sx={{ height: '100%', minHeight: isDesktop ? undefined : 44 }}><CardContent>
                <Stack direction="row" spacing={1.25}><RequestTypeIcon type={request.Type} small /><Box sx={{ minWidth: 0, flex: 1 }}><Stack direction="row" justifyContent="space-between"><Typography variant="caption" color="text.secondary">REQ-{request.Number}</Typography><Typography variant="caption" color="text.secondary">{dayjs(request.LastUpdateDate).format('MMM D')}</Typography></Stack><Typography variant="h6" noWrap>{request.Title}</Typography><Typography variant="body2" color="text.secondary" noWrap>{request.OrganizationName} · {request.ReporterName}</Typography></Box></Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, minHeight: 40, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{request.Description}</Typography>
                <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 1.5 }}><StatusChip status={request.Status} /><PriorityChip priority={request.Priority} />{request.AssignedToName && <Chip size="small" icon={<PeopleAltRoundedIcon />} label={request.AssignedToName} />}</Stack>
            </CardContent></CardActionArea></Card></Grid>)}
            {requests.length === 0 && <Grid item xs={12}><Alert severity="info">No requests match the current filters.</Alert></Grid>}
        </Grid>
    </Stack>;
};

const OrganizationDirectory = ({ organizations, query, setQuery, onSetSubscriptionNotRequired }: {
    organizations: AdminOrganizationSummary[];
    query: string;
    setQuery: (value: string) => void;
    onSetSubscriptionNotRequired: (organization: AdminOrganizationSummary, enabled: boolean) => void;
}) => {
    const { mode, isDesktop, isTablet, isLandscape } = useViewportMode();
    return <Stack spacing={2} data-viewport-mode={mode}>
        <Alert severity="info">Subscription-free access grants the organization full entitlement and removes subscription controls for its users. Changes take effect the next time their access token refreshes.</Alert>
        <TextField size="small" label="Search organizations" value={query} onChange={event => setQuery(event.target.value)} sx={{ width: isDesktop ? undefined : '100%', maxWidth: isDesktop ? 480 : 'none', '& .MuiInputBase-root': { minHeight: isDesktop ? undefined : 44 } }} />
        {isDesktop ? <TableContainer component={Card} variant="outlined"><Table size="small"><TableHead><TableRow><TableCell>Organization</TableCell><TableCell>Subscription</TableCell><TableCell>Free access</TableCell><TableCell align="right">Teammates</TableCell><TableCell align="right">Users</TableCell><TableCell align="right">Open requests</TableCell><TableCell align="right">Est. MRR</TableCell><TableCell>Last activity</TableCell></TableRow></TableHead><TableBody>
            {organizations.map(organization => <TableRow key={organization.Id} hover><TableCell><Typography fontWeight={700}>{organization.Name}</Typography><Typography variant="caption" color="text.secondary">{organization.Id}</Typography></TableCell><TableCell><Chip size="small" color={organization.SubscriptionNotRequired ? 'info' : organization.IsSubscriptionActive ? 'success' : 'default'} label={organization.SubscriptionNotRequired ? 'Not required' : organization.SubscriptionStatus || (organization.IsSubscriptionActive ? 'Active' : 'Inactive')} /></TableCell><TableCell><Switch size="small" checked={organization.SubscriptionNotRequired} inputProps={{ 'aria-label': `Subscription-free access for ${organization.Name}` }} onChange={(_, checked) => onSetSubscriptionNotRequired(organization, checked)} /></TableCell><TableCell align="right">{organization.ActiveTeammateCount} active / {organization.BilledTeammateCount} billed</TableCell><TableCell align="right">{organization.UserCount}</TableCell><TableCell align="right">{organization.OpenRequestCount}</TableCell><TableCell align="right"><Typography fontWeight={800}>{money(organization.EstimatedMonthlyRevenueCents)}</Typography></TableCell><TableCell>{organization.LastActivityDate ? dayjs(organization.LastActivityDate).format('MMM D, YYYY') : '—'}</TableCell></TableRow>)}
        </TableBody></Table></TableContainer> : <Grid container spacing={1.5}>
            {organizations.map(organization => <Grid item xs={12} sm={isTablet && isLandscape ? 6 : 12} key={organization.Id}>
                <MobileEntityCard
                    ariaLabel={`Organization ${organization.Name}`}
                    title={organization.Name}
                    subtitle={organization.Id}
                    status={<Chip size="small" color={organization.SubscriptionNotRequired ? 'info' : organization.IsSubscriptionActive ? 'success' : 'default'} label={organization.SubscriptionNotRequired ? 'Not required' : organization.SubscriptionStatus || (organization.IsSubscriptionActive ? 'Active' : 'Inactive')} />}
                    fields={[
                        { label: 'Teammates', value: `${organization.ActiveTeammateCount} active / ${organization.BilledTeammateCount} billed` },
                        { label: 'Users', value: organization.UserCount },
                        { label: 'Open requests', value: organization.OpenRequestCount },
                        { label: 'Est. MRR', value: money(organization.EstimatedMonthlyRevenueCents) },
                        { label: 'Last activity', value: organization.LastActivityDate ? dayjs(organization.LastActivityDate).format('MMM D, YYYY') : '—', fullWidth: true },
                    ]}
                    actions={<Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="body2">Subscription-free access</Typography>
                        <Switch
                            checked={organization.SubscriptionNotRequired}
                            inputProps={{ 'aria-label': `Subscription-free access for ${organization.Name}` }}
                            onChange={(_, checked) => onSetSubscriptionNotRequired(organization, checked)}
                        />
                    </Stack>}
                />
            </Grid>)}
        </Grid>}
    </Stack>;
};

const RequestTriageDialog = ({ request, assignees, onClose, onRefresh, onBusy, onError }: {
    request?: SupportRequestSummary;
    assignees: SupportAssignee[];
    onClose: () => void;
    onRefresh: (id: string) => Promise<void>;
    onBusy: (busy: boolean) => void;
    onError: (error: any, title: string) => void;
}) => {
    const { mode, isPhone, isTablet, isPortrait, isDesktop } = useViewportMode();
    const singleColumn = isPhone || (isTablet && isPortrait);
    const [status, setStatus] = useState<SupportRequestStatus>('Submitted');
    const [priority, setPriority] = useState<SupportRequestPriority>('Normal');
    const [transitionReason, setTransitionReason] = useState('');
    const [assignment, setAssignment] = useState('');
    const [duplicateOfRequestId, setDuplicateOfRequestId] = useState('');
    const [comment, setComment] = useState('');
    const [internal, setInternal] = useState(true);
    useEffect(() => { if (request) { setStatus(request.Status); setPriority(request.Priority); setAssignment(request.AssignedToSystemUserId || ''); setDuplicateOfRequestId(''); setTransitionReason(''); setComment(''); } }, [request]);

    const run = async (action: () => Promise<any>, title: string) => {
        if (!request) return;
        try { onBusy(true); await action(); await onRefresh(request.Id); }
        catch (error) { onError(error, title); }
        finally { onBusy(false); }
    };
    return <ResponsiveDialog open={!!request} onClose={onClose} fullWidth maxWidth="lg" tabletFullScreen="portrait">
        {request && <>
            <DialogTitle sx={{ pb: 1 }}><Stack direction="row" spacing={1.5} alignItems="center"><RequestTypeIcon type={request.Type} /><Box sx={{ minWidth: 0 }}><Typography variant="overline" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>REQ-{request.Number} · {request.OrganizationName}</Typography><Typography variant={isPhone ? 'h5' : 'h4'} sx={{ overflowWrap: 'anywhere' }}>{request.Title}</Typography></Box></Stack></DialogTitle>
            <DialogContent dividers sx={{ px: isPhone ? 2 : 3 }}><Grid container spacing={3}>
                <Grid item xs={12} md={singleColumn ? 12 : 7}><Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{request.Description}</Typography><Stack direction="row" gap={1} flexWrap="wrap" sx={{ my: 2 }}><StatusChip status={request.Status} /><PriorityChip priority={request.Priority} /></Stack><Divider sx={{ my: 2 }} /><Typography variant="h5" sx={{ mb: 2 }}>Complete activity</Typography><ActivityTimeline activity={request.Activity} showInternal /></Grid>
                <Grid item xs={12} md={singleColumn ? 12 : 5}><Stack spacing={2} sx={{ '& .MuiButtonBase-root, & .MuiInputBase-root': { minHeight: isDesktop ? undefined : 44 } }}>
                    <Alert severity="info">Client-visible replies appear in the organization’s Requests page. Internal notes never leave System Administration.</Alert>
                    <FormControl><InputLabel>Status</InputLabel><Select label="Status" value={status} onChange={event => setStatus(event.target.value as SupportRequestStatus)}>{[request.Status, ...allowedTransitions[request.Status]].map(item => <MenuItem key={item} value={item}>{supportStatusLabel(item)}</MenuItem>)}</Select></FormControl>
                    <FormControl><InputLabel>Priority</InputLabel><Select label="Priority" value={priority} onChange={event => setPriority(event.target.value as SupportRequestPriority)}>{requestPriorities.map(item => <MenuItem key={item} value={item}>{item}</MenuItem>)}</Select></FormControl>
                    <TextField label="Transition reason" multiline minRows={2} value={transitionReason} onChange={event => setTransitionReason(event.target.value)} />
                    {status === 'Duplicate' && <TextField required label="Canonical request ID" value={duplicateOfRequestId} onChange={event => setDuplicateOfRequestId(event.target.value)} helperText="Paste the request ID from the canonical request." />}
                    <Button variant="contained" disabled={status === 'Duplicate' && !duplicateOfRequestId.trim()} onClick={() => run(() => new SupportClient().TransitionRequest(request.Id, status, priority, request.RowVersion, transitionReason, duplicateOfRequestId), 'Unable to update request')}>Apply triage update</Button>
                    <Divider />
                    <FormControl><InputLabel>Assignee</InputLabel><Select label="Assignee" value={assignment} onChange={event => setAssignment(event.target.value)}><MenuItem value="">Unassigned</MenuItem>{assignees.map(assignee => <MenuItem key={assignee.Id} value={assignee.Id}>{assignee.Name || assignee.EmailAddress}</MenuItem>)}</Select></FormControl>
                    <Button variant="outlined" disabled={assignment === (request.AssignedToSystemUserId || '')} onClick={() => run(() => new SupportClient().AssignRequest(request.Id, assignment || undefined, request.RowVersion), 'Unable to assign request')}>Update assignment</Button>
                    <Divider />
                    <ToggleButtonGroup fullWidth exclusive value={internal ? 'internal' : 'public'} onChange={(_, value) => value && setInternal(value === 'internal')}><ToggleButton value="internal">Internal note</ToggleButton><ToggleButton value="public">Client reply</ToggleButton></ToggleButtonGroup>
                    <TextField label={internal ? 'Internal note' : 'Reply to client'} multiline minRows={3} value={comment} onChange={event => setComment(event.target.value)} />
                    <Button variant="contained" color={internal ? 'warning' : 'primary'} disabled={!comment.trim()} onClick={() => run(async () => { await new SupportClient().AddAdminComment(request.Id, comment.trim(), internal, request.RowVersion); setComment(''); }, 'Unable to add comment')}>Post {internal ? 'internal note' : 'client reply'}</Button>
                </Stack></Grid>
            </Grid></DialogContent>
            <StickyDialogActions phoneLayout="inline" viewportOverride={mode}><Button onClick={onClose}>Close</Button></StickyDialogActions>
        </>}
    </ResponsiveDialog>;
};

export default SystemAdministrationPage;
